import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import { SEX_LIST } from '@/common/constants/common';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppEstimatedAgeTextField from '@/common/components/form-inputs/AppEstimatedAgeTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditReferralForICCMWForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {
			sex: null,
		};

		useEffect(() => {
			if (details) {
				methods.reset(details);
			}
		}, [details]);

		const formSchema = Yup.object().shape({
			nameEn: Yup.string().required(t('errorMessage.pleaseEnter')),
			dateOfBirth: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.referralForICCMW')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameEn'
									label={t('preAdmission.name_en') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.sex')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='sex'
									row
									options={SEX_LIST}
								/>
							</Grid>
							<Grid size={6}>
								<AppEstimatedAgeTextField
									control={methods.control}
									label={t('preAdmission.age')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='dateOfBirth'
									label={t('preAdmission.dateOfBirth')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='fullAddress'
									label={t('preAdmission.address')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditReferralForICCMWForm.displayName = 'EditReferralForICCMWForm';

export default EditReferralForICCMWForm;
